import { Worm } from '../entities/worm';
import { getTankType, DEFAULT_TANK_TYPE } from '../core/tankTypes';

export interface TeamConfig {
  id: string;
  name: string;
  color: number;
  wormNames: string[];
  /** Alliance group; teams sharing an alliance win and lose together. */
  allianceId?: string;
  /** GunBound tank/mobile type id (see core/tankTypes.ts). Defaults to 'worm'. */
  tankType?: string;
}

export const DEFAULT_AMMO: Record<string, number> = {
  bazooka: -1,
  grenade: 10,
  grub: 4,
  grubSS: 1,
  aduka: 2,
  nak: 2,
  adukaSS: 1,
  lightningT1: 4,
  lightningT2: 2,
  knight: 2,
  trico: 2,
  kalsiddon: 2,
  frogLeft: 2,
  frogRight: 2,
  raon: 2,
  jdT2: 2,
  jdSS: 1,
  cluster: 5,
  shotgun: -1,
  dynamite: 5,
  mine: 2,
  airstrike: 0,
  homing: 2,
  teleport: 2,
  rope: -1,
  parachute: 2,
  firePunch: 3,
  baseballBat: 3,
  blowtorch: 2,
  drill: 2,
  holyHandGrenade: 0,
  sheep: 1,
  banana: 0,
  mortar: 3,
  molotov: 3,
  napalmStrike: 0,
  kamikaze: 2,
  bungee: 2,
  jetpack: 2,
  girder: 3,
  supersheep: 1,
};

export class Team {
  id: string;
  name: string;
  color: number;
  /** Teams sharing an alliance win and lose together (defaults to own id). */
  allianceId: string;
  /** GunBound tank/mobile type id (drives weapon preset, stats, sprite). */
  tankTypeId: string;
  /** Per-turn walk budget (px) for this team's tank; undefined => inherit the
   *  mode budget. null = unlimited. Set from the tank type at setup. */
  movementBudgetPx?: number | null;
  worms: Worm[] = [];
  currentWormIndex = 0;
  inventory: Map<string, number> = new Map();
  /** In-match consumable items (itemId -> remaining count). */
  items: Map<string, number> = new Map();

  constructor(config: TeamConfig) {
    this.id = config.id;
    this.name = config.name;
    this.color = config.color;
    this.allianceId = config.allianceId ?? config.id;
    this.tankTypeId = config.tankType ?? DEFAULT_TANK_TYPE;
    this.initializeInventory();
  }

  private initializeInventory(): void {
    for (const [weaponId, ammo] of Object.entries(DEFAULT_AMMO)) {
      this.inventory.set(weaponId, ammo);
    }
  }

  setInfiniteAmmo(): void {
    for (const weaponId of this.inventory.keys()) {
      this.inventory.set(weaponId, -1);
    }
  }

  getAmmo(weaponId: string): number {
    return this.inventory.get(weaponId) ?? 0;
  }

  hasAmmo(weaponId: string): boolean {
    const ammo = this.getAmmo(weaponId);
    return ammo === -1 || ammo > 0;
  }

  useAmmo(weaponId: string): boolean {
    const current = this.getAmmo(weaponId);
    if (current === -1) return true;
    if (current <= 0) return false;
    this.inventory.set(weaponId, current - 1);
    return true;
  }

  addAmmo(weaponId: string, amount: number): void {
    const current = this.getAmmo(weaponId);
    if (current === -1) return;
    this.inventory.set(weaponId, current + amount);
  }

  getInventorySnapshot(): Record<string, number> {
    const snapshot: Record<string, number> = {};
    for (const [id, ammo] of this.inventory) {
      snapshot[id] = ammo;
    }
    return snapshot;
  }

  applyInventorySnapshot(snapshot: Record<string, number>): void {
    for (const [id, ammo] of Object.entries(snapshot)) {
      this.inventory.set(id, ammo);
    }
  }

  addWorm(worm: Worm): void {
    this.worms.push(worm);
  }

  /** Zero out ammo for every weapon not in the whitelist. */
  restrictInventory(whitelist: string[]): void {
    for (const weaponId of this.inventory.keys()) {
      if (!whitelist.includes(weaponId)) {
        this.inventory.set(weaponId, 0);
      }
    }
  }

  /** Replace the whole inventory with a tank's weapon preset: every weapon not
   *  in the preset is zeroed; preset weapons take their preset ammo. This is
   *  the authoritative weapon set when the mode enables tank selection. */
  applyWeaponPreset(preset: Record<string, number>): void {
    for (const weaponId of this.inventory.keys()) {
      this.inventory.set(weaponId, preset[weaponId] ?? 0);
    }
    for (const [weaponId, ammo] of Object.entries(preset)) {
      this.inventory.set(weaponId, ammo);
    }
  }

  /** Configure in-match item counts (from mode.items.matchItems). */
  setMatchItems(counts: Record<string, number>): void {
    this.items.clear();
    for (const [itemId, count] of Object.entries(counts)) {
      if (Number.isFinite(count) && count > 0) {
        this.items.set(itemId, Math.floor(count));
      }
    }
  }

  getItemCount(itemId: string): number {
    return this.items.get(itemId) ?? 0;
  }

  /** Consume one item; false when none left. */
  useItem(itemId: string): boolean {
    const current = this.getItemCount(itemId);
    if (current <= 0) return false;
    this.items.set(itemId, current - 1);
    return true;
  }

  getItemsSnapshot(): Record<string, number> {
    const snapshot: Record<string, number> = {};
    for (const [id, count] of this.items) {
      snapshot[id] = count;
    }
    return snapshot;
  }

  applyItemsSnapshot(snapshot: Record<string, number>): void {
    for (const [id, count] of Object.entries(snapshot)) {
      this.items.set(id, count);
    }
  }

  getCurrentWorm(): Worm | null {
    const aliveWorms = this.getAliveWorms();
    if (aliveWorms.length === 0) return null;
    return aliveWorms[this.currentWormIndex % aliveWorms.length];
  }

  advanceWorm(): void {
    const aliveWorms = this.getAliveWorms();
    if (aliveWorms.length > 0) {
      this.currentWormIndex = (this.currentWormIndex + 1) % aliveWorms.length;
    }
  }

  getAliveWorms(): Worm[] {
    return this.worms.filter((w) => w.health > 0 && w.state !== 'dead');
  }

  getTotalHealth(): number {
    return this.worms.reduce((sum, w) => sum + Math.max(0, w.health), 0);
  }

  isEliminated(): boolean {
    return this.getAliveWorms().length === 0;
  }
}

export interface CreateTeamOptions {
  initialHealth?: number;
  aimLimits?: { min: number; max: number };
  /** GunBound: aim rotates with the ground slope under the tank. */
  aimRelativeToGround?: boolean;
  /** When true (GunBound), apply the team's tank-type weapon preset + stats
   *  (HP, movement). When false (classic), tanks are ignored. */
  applyTankType?: boolean;
}

export function createTeam(
  config: TeamConfig,
  spawnPoints: Array<{ x: number; y: number }>,
  options?: CreateTeamOptions,
): Team {
  const team = new Team(config);

  // GunBound tank type: weapon preset + per-tank stats (HP, movement). The
  // tank's weapon preset is the authoritative set (replaces the mode whitelist).
  const tank = options?.applyTankType ? getTankType(team.tankTypeId) : null;
  if (tank) {
    team.applyWeaponPreset(tank.ammoPreset);
    team.movementBudgetPx = tank.stats.movementBudgetPx;
  }
  const tankHealth = tank?.stats.maxHealth;
  const initialHealth = tankHealth ?? options?.initialHealth;

  // Identify the team by its first worm's name (falls back to the configured
  // team name when there are no worms). Applies to every mode.
  const firstWormName = config.wormNames[0]?.trim();
  if (firstWormName) {
    team.name = firstWormName;
  }

  config.wormNames.forEach((name, index) => {
    const spawn = spawnPoints[index] || { x: 100 + index * 50, y: 100 };
    const worm = new Worm(name, team.id, spawn.x, spawn.y, initialHealth);
    worm.id = `${team.id}_worm_${index}`;
    if (options?.aimLimits) {
      worm.setAimLimits(options.aimLimits.min, options.aimLimits.max);
    }
    worm.aimRelativeToGround = options?.aimRelativeToGround ?? false;
    team.addWorm(worm);
  });

  return team;
}
